import IssueProvider from '@adsk/issues-ui/es/IssueProvider';
import IssueCreateModal from '@adsk/issues-ui/es/IssueCreateModal';
import React from 'react';
import { useLocale } from '@adsk/bim360-matrix-react-helpers/es/locale';
import { MDText } from 'i18n-react';
import PropTypes from 'prop-types';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import { ISSUES_ENV_TYPES } from '@adsk/issues-ui/es/constants/envs';
import { REGIONS } from '@adsk/bim360-matrix-react-helpers/es/consts';

import {
  oxygenUserType,
  roleType,
  companyType,
  initialFieldValuesType
} from '@adsk/issues-ui/es/constants/proptype-validation/issue-proptypes';
import './styles/_issueCreateFormsStyles.scss';
import { ENTITY_TYPES } from '@adsk/bim360-matrix-react-entity-linker';
import {
  NotificationTypes,
  NotificationLevels
} from '@adsk/bim360-matrix-react-notification-manager/es/NotificationManager';
import { useNotification } from '@adsk/bim360-matrix-react-notification-manager/es/helpers/context';
import { currentProjectEntitlementsType, currentUserType } from './constants/proptypes';
import IssueCreateNewEntityWrapper from './IssueCreateNewEntityWrapper';

const IssueCreateNewEntityForm = ({
  env,
  region,
  currentProjectEntitlements,
  currentUser,
  usersInProject,
  rolesInProject,
  companiesInProject,
  onClose,
  onCreateSuccess,
  initialFields,
  apigeeUrl,
  hostingAppName,
  isOpen,
  container
}) => {
  const { T, locale } = useLocale(({ locale }) => new MDText(require(`./i18n/${locale}.json`)));
  const { showNotification } = useNotification();
  const handleSaveSuccess = ({ response }) => {
    onCreateSuccess({ ...response, entityType: ENTITY_TYPES.ISSUE });
    showNotification({
      type: NotificationTypes.TOAST,
      level: NotificationLevels.SUCCESS,
      message: T.translate('CREATE_SUCCESS')
    });
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="issues-entity-linker">
      <IssueProvider
        env={ISSUES_ENV_TYPES[env]}
        region={region}
        locale={locale}
        currentProjectEntitlements={currentProjectEntitlements}
        currentUser={currentUser}
        users={usersInProject}
        companies={companiesInProject}
        roles={rolesInProject}
        hostingAppName={hostingAppName}
        apigeeUrl={apigeeUrl}
      >
        {isOpen && (
          <IssueCreateNewEntityWrapper>
            <IssueCreateModal
              container={container}
              isOpen={true}
              onClose={handleClose}
              onSuccess={handleSaveSuccess}
              data-testid="IssueCreateModal"
              className="issues-entity-create-modal"
              initialFieldValues={{ ...initialFields }}
            />
          </IssueCreateNewEntityWrapper>
        )}
      </IssueProvider>
    </div>
  );
};
IssueCreateNewEntityForm.displayName = 'IssueCreateNewEntityForm';
IssueCreateNewEntityForm.propTypes = {
  // These props are coming from EntityLinker

  /**
   * Callback that's being called when closing the modal.
   */
  onClose: PropTypes.func.isRequired,
  /**
   * Callback that's being called when creating issue successfully.
   */
  onCreateSuccess: PropTypes.func.isRequired,
  /**
   * The ENV - one of `ISSUES_ENV_TYPES` values
   */
  env: PropTypes.oneOf(objectValues(ISSUES_ENV_TYPES)).isRequired,
  /**
   * The region. One of `REGIONS` values.
   */
  region: PropTypes.oneOf(objectValues(REGIONS)).isRequired,
  /**
   * To show or close create issue modal
   */
  isOpen: PropTypes.bool,
  /**
   * Modal container
   */
  container: PropTypes.object,
  // These props should be passed from EntityLinker through createFormProps

  /**
   * Current project entitlements. Should be in the shape of `@adsk/bim360-mc-interface/es/models/EntitlementProjectItem`
   */
  currentProjectEntitlements: currentProjectEntitlementsType.isRequired,
  /**
   * The current user
   */
  currentUser: currentUserType.isRequired,
  /**
   * Users in current project. Should be in the shape of `@adsk/bim360-mc-interface/es/models/User`
   */
  usersInProject: PropTypes.arrayOf(oxygenUserType).isRequired,
  /**
   * Roles in current project. Should be in the shape of `@adsk/bim360-mc-interface/es/models/Role`
   */
  rolesInProject: PropTypes.arrayOf(roleType).isRequired,
  /**
   * Companies in current project. Should be in the shape of `@adsk/bim360-mc-interface/es/models/Company`
   */
  companiesInProject: PropTypes.arrayOf(companyType).isRequired,
  /**
   * origin to be tracked in analytics.
   */
  origin: PropTypes.string,
  /**
   * Initial fields to pre populate the form
   */
  initialFields: initialFieldValuesType,
  /**
   * Optional parameter that will override the apigee server address of all apigee calls.
   */
  apigeeUrl: PropTypes.string,
  /**
   * Hosting app name - used for analytics and FF.
   */
  hostingAppName: PropTypes.string.isRequired
};
IssueCreateNewEntityForm.defaultProps = {
  container: document.body
};
export default IssueCreateNewEntityForm;
